import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { Scenarios } from '@gql/base-types';
import ScenarioTile from '@components/Timeline/Tile/ScenarioTile';
import { getTimingMove, hasRideMove, type Scenario as SdkScenario } from '@hopdrive/sdk-appointments';

interface ScheduleOfferedScenarioBlockProps {
  scenario: Scenarios;
  leftPosition: number; // pixels from start of day
  onClick: (scenario: Scenarios) => void;
  zoom?: number;
}

const ScheduleOfferedScenarioBlock = ({
  scenario,
  leftPosition,
  onClick,
  zoom = 4
}: ScheduleOfferedScenarioBlockProps) => {
  const theme = useTheme();

  // Check if we need to render ghost travel blocks
  const shouldShowTravelBlock =
    (scenario.scenario_key === 'concierge-pu' || scenario.scenario_key === 'concierge-ret') && !hasRideMove(scenario);

  // Get travel duration for ghost block
  let travelDurationMin = 0;
  let travelWidthPx = 0;

  if (shouldShowTravelBlock) {
    const timingMove = getTimingMove(scenario as unknown as SdkScenario);
    if (timingMove?.lane) {
      // Ghost blocks show the "extra" travel time not represented by the blue tile:
      // - concierge-pu: Ghost "Travel" block BEFORE = travel TO customer (inverse duration)
      // - concierge-ret: Ghost "Return" block AFTER = return TO dealer (forward duration)
      const laneDuration = scenario.scenario_key === 'concierge-pu'
        ? timingMove.lane.inverse?.duration_sec || timingMove.lane.duration_sec
        : timingMove.lane.duration_sec; // concierge-ret uses forward duration for return trip

      if (laneDuration) {
        travelDurationMin = Math.round(laneDuration / 60);
        travelWidthPx = travelDurationMin * zoom;
      }
    }
  }

  // Adjust leftPosition ONLY for concierge-pu to account for ghost travel block rendered before appointment
  // The ghost block is visual-only but rendered in flexbox before the tile, so we need to shift the container left
  // concierge-ret doesn't need adjustment because the blue tile IS the travel to customer, Return block is AFTER
  // concierge-loaner-pu doesn't have ghost blocks at all (uses real moves)
  const adjustedLeftPosition =
    scenario.scenario_key === 'concierge-pu' && shouldShowTravelBlock && travelWidthPx > 0
      ? leftPosition - travelWidthPx
      : leftPosition;

  // Extract offer status from scenario
  const offerStatus = React.useMemo(() => {
    const bundleLinks = scenario.bundletoscenarios || [];
    for (const bundleLink of bundleLinks) {
      const offers = bundleLink.bundle?.offers || [];
      for (const offer of offers) {
        if (offer.status === 'offered' || offer.status === 'accepted') {
          return offer.status;
        }
      }
    }
    return null;
  }, [scenario]);

  // Determine styling based on status
  const { borderColor, opacity, badgeColor, badgeLabel } = React.useMemo(() => {
    if (offerStatus === 'offered') {
      return {
        borderColor: '#FF9800', // Orange
        opacity: 0.7,
        badgeColor: '#FF9800',
        badgeLabel: 'OFFERED'
      };
    } else if (offerStatus === 'accepted') {
      return {
        borderColor: '#2196F3', // Blue
        opacity: 0.8,
        badgeColor: '#2196F3',
        badgeLabel: 'ACCEPTED'
      };
    }
    return {
      borderColor: 'transparent',
      opacity: 1,
      badgeColor: 'transparent',
      badgeLabel: ''
    };
  }, [offerStatus]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${adjustedLeftPosition}px`,
        top: 8,
        display: 'flex',
        flexDirection: 'row',
        opacity: opacity,
      }}
    >
      {/* Ghost travel block BEFORE appointment (concierge-pu only) */}
      {/* For concierge-pu: driver travels TO customer before the appointment/pickup */}
      {shouldShowTravelBlock && scenario.scenario_key === 'concierge-pu' && travelWidthPx > 0 && (
        <Box
          sx={{
            width: `${travelWidthPx}px`,
            height: '64px',
            background: `repeating-linear-gradient(
              45deg,
              ${theme.palette.warning.light}40,
              ${theme.palette.warning.light}40 10px,
              ${theme.palette.warning.main}20 10px,
              ${theme.palette.warning.main}20 20px
            )`,
            borderRadius: '8px',
            border: `2px solid ${theme.palette.warning.main}60`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '4px',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 600,
              color: theme.palette.warning.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              userSelect: 'none',
            }}
          >
            Travel
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: theme.palette.warning.dark,
              userSelect: 'none',
              lineHeight: 1,
            }}
          >
            {travelDurationMin} min
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          borderRadius: '10px',
          padding: '2px',
        }}
      >
        {/* Status badge */}
        <Chip
          label={badgeLabel}
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: 8,
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 600,
            bgcolor: badgeColor,
            color: 'white',
            zIndex: 10,
            '& .MuiChip-label': {
              px: 1,
            }
          }}
        />

        {/* Scenario tile */}
        <ScenarioTile
          scenario={scenario}
          onClick={() => onClick(scenario)}
          size="medium"
          zoom={zoom}
        />
      </Box>

      {/* Ghost travel block AFTER appointment (concierge-ret only) */}
      {/* For concierge-ret: driver returns to dealer AFTER dropping off the car */}
      {shouldShowTravelBlock && scenario.scenario_key === 'concierge-ret' && travelWidthPx > 0 && (
        <Box
          sx={{
            width: `${travelWidthPx}px`,
            height: '64px',
            background: `repeating-linear-gradient(
              45deg,
              ${theme.palette.warning.light}40,
              ${theme.palette.warning.light}40 10px,
              ${theme.palette.warning.main}20 10px,
              ${theme.palette.warning.main}20 20px
            )`,
            borderRadius: '8px',
            border: `2px solid ${theme.palette.warning.main}60`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '4px',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 600,
              color: theme.palette.warning.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              userSelect: 'none',
            }}
          >
            Return
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: theme.palette.warning.dark,
              userSelect: 'none',
              lineHeight: 1,
            }}
          >
            {travelDurationMin} min
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ScheduleOfferedScenarioBlock;
